/* Purpose: All loan files can be viewed / uploaded / deleted from this form
 * Props: loanRequFileList (list of maps)
 *          loanBasicInfo (map)
 *          handleFileChange (func)
 *          isUploadingFile (list)
 *          isSavingFile (boolean)
 *          previewFileCache (list)
 *          setPreviewFileCache (setState)
 *          handleSelectChange (func)
 *          setClickedRemove (setState)
 *          setClickedPreview (setState)
 *          selectedFiles (map)
 *          setSelectedFiles (setState)
 *          handleGetFromOtherLoan (func)
 *          showModal (boolean)
 *          ClientID (str)
 *          setShowModal (setState)
 *          credentials (map)
 *          isOccupied (boolean)
 *          back (func)
 * Author: R. Markowitz (April 2025)
 * Dev Notes: 
*/

import React, {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import SubmitButton, { DownloadFileButton} from "./Button";
import { Container, Form, Row, Col,Stack, Spinner, Image, DropdownButton, Dropdown } from "react-bootstrap";
import FilesModal from "./AvailableFilesModal";

const LoanFiles = ({loanRequFileList, loanBasicInfo, handleFileChange, isUploadingFile, isSavingFile, previewFileCache, setPreviewFileCache, setSelectedFiles, setClickedRemove, selectedFiles, back, handleGetFromOtherLoan, showModal, ClientID, setShowModal, credentials, isOccupied}) => {

    const viewIcon = require("../Images/viewSmall.png");
    const deleteIcon = require("../Images/deleteSmall.png");
    const onlyIfOccupied = ["Lease Agreement", "Rent Verification", "Security Deposit Receipt"];
    const [clickedPreview, setClickedPreview] = useState("");

    useEffect(() => {
      window.scrollTo({top: 0, left: 0, behavior: 'smooth' });
    }, [])

    // When a file is selected form the uploaded files form select, set that file to the value for the fileName in selectedFiles array
    const handleSelectChange = (fileName, selectedValue) => {
        let tempSelectedFiles = {...selectedFiles, [fileName]:selectedValue};
        setSelectedFiles(tempSelectedFiles);
    }

    useEffect (() => {
        const handleClickedPreview = async (filePath) => {

        try{
            const urlResponse = await fetch('https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/downloadFileFromS3', {
            "method": "POST",
            "body":JSON.stringify({
                "filePath":filePath,
                "credentials": credentials
            })
            });
            if (urlResponse.ok){
            const presignedData = await urlResponse.json();
            const fetchedFileResponse = await fetchFileFromS3(presignedData, filePath);

            }else{
            console.log("Something went wrong when trying to get a presigned url. response not ok.")
            alert("An error occured while retireiving the file for viewing.")
            }
        }catch (error){
            console.error("Something went wrong when getting or using the presigned url to view the file. Error: ", error)
            alert("An error occured while retireiving the file for viewing.")
        }        
        };

        const fetchFileFromS3 = async (urlData, fileName) => {
        try{     
            const response = await fetch(urlData, {responseType: "arrayBuffer"});
            
            if (response.ok){
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            // cache the file
            setPreviewFileCache([...previewFileCache, {url, name: fileName}]);


            }else{
            console.error("Something went wrong when fetching the file. Response not ok!")
            alert("An error occured while retireiving the file for viewing.")
            }

        }catch (error){
            console.error("Something went wrong when trying to view the file.",  error)
            alert("An error occured while retireiving the file for viewing.")
        }
        }

        if (loanRequFileList.length > 0 && clickedPreview !== "" && selectedFiles[loanRequFileList[clickedPreview].fileName] !== "Uploaded File(s)"){
            let isSelectedFilesUpdated = ((prevState) => prevState !== selectedFiles);
            if (isSelectedFilesUpdated && selectedFiles[loanRequFileList[clickedPreview].fileName] !== "Uploaded File(s)"){
                const filePath = selectedFiles[loanRequFileList[clickedPreview].fileName];
                const cacheIndex =  previewFileCache.findIndex(f => f.name === filePath);
                
                // If the file is cached, open it from fileCache.
                // If not, call handleClickedPreview to fetch from s3
                if (cacheIndex !== -1){
                    console.log("Opening file from cache...");
                    let url  = previewFileCache[cacheIndex].url
                    window.open(url, '_blank');
                }else{
                    console.log("File not cached. Fetching file from S3...");
                    handleClickedPreview(filePath);
                }

            setClickedPreview("");        
            }else{
                console.log("Could not preview file - Something went wrong")
                alert("An error occured while retireiving the file for viewing.")
            }
        }else if(loanRequFileList && clickedPreview !== "" && selectedFiles[loanRequFileList[clickedPreview].fileName] === "Uploaded File(s)"){
            console.log("Could not preview file - Selected Files is not updated...")
            alert("No file is selected. Please choose a file before trying to preview. ")
        }
    },[clickedPreview, selectedFiles]);



    return(
        <Container className = 'm-3'>
            {loanRequFileList.map((item, index) => (
                <>
                {(!onlyIfOccupied.includes(item.fileName) || (onlyIfOccupied.includes(item.fileName) && isOccupied)) && (
                <Row className='file-row'>
                    <Col className='w-48'>
                        <Form.Label id = {item.fileName}><strong>{item.fileName}</strong></Form.Label>
                        <br/>
                        {item.toolTip &&(
                            <Form.Text id = {item.fileName + " tooltip"} muted>  {item.toolTip}</Form.Text>
                        )}
                        {(loanBasicInfo.ClientFeedback && loanBasicInfo.ClientFeedback[item.fileName]) && (
                            <Row>
                                <Form.Text id = {item.fileName + " feedback"}  className="client-feedback">
                                    Comments: {loanBasicInfo.ClientFeedback[item.fileName].map((note, index) => (
                                        <li key = {index}>{note}</li>
                                    ))}
                                </Form.Text>
                            </Row>
                        )}
                    </Col>
                    <Col className='w-52'>
                        <Stack direction="horizontal" gap={4} key={index}>
                            <Col sm={1} className='mx-auto'>
                                {isUploadingFile && isUploadingFile.includes(index) && (
                                    <Spinner
                                        as="span"
                                        animation='border'
                                        role='status'
                                        aria-hidden = 'true'
                                    >
                                        <span className='visually-hidden'>Loading...</span>
                                    </Spinner>
                                    
                                )}
                            </Col>
                            <div className = "stack-file-control m-2" style = {item.value.length == 0 || item.maxUploads == "multiple"? ({visibility:"visible"}):({visibility:"hidden"})} >
                                    <Form.Control 
                                    as = "input"
                                    type="file" 
                                    size="sm" 
                                    style={{ backgroundColor: item.value.length == 0 ? '#b85c5c' : 'transparent' }} 
                                    multiple = {item.maxUploads === "multiple"}
                                    onChange={(e) => handleFileChange(index, e)} 
                                    disabled = {isUploadingFile.length > 0 || isSavingFile}
                                    accept=''
                                    id = {item.fieldName}
                                    />

                                {!item.template == ""?(
                                    <DownloadFileButton filePath = {item.template} text = {item.templateText} />
                                ):(<></>)   
                                }  
                                <DropdownButton 
                                id = {item.fileName + "-GFAL"} 
                                title = "Get From Another Loan" 
                                variation = "light"
                                size = "sm"
                                style = {(item.value.length == 0 || item.maxUploads == "multiple") && item.hasFromPortalOption && item.hasFromPortalOption === "yes"? ({visibility:"visible"}):({visibility:"hidden"})}
                                >
                                {item.getFromOptions && item.getFromOptions.map((option, optionIndex) => 
                                (
                                    <>
                                        <Dropdown.Item 
                                            key = {optionIndex} 
                                            onClick = {() => handleGetFromOtherLoan(item, index, option)}
                                            disabled = {option == "There are no loans with this file on record."?true:false}
                                        >{option} </Dropdown.Item>
                                    </>
                                )) } 
                                

                                {showModal &&(
                                    <FilesModal 
                                        ClientID = {ClientID} 
                                        LoanID = {showModal}
                                        fieldName = {item.fileName}
                                        currentFiles = {item.value}
                                        onHide = {() => setShowModal(false)} 
                                        onUseFile = {[handleGetFromOtherLoan, item, index]}
                                        credentials = {credentials}
                                    />
                                )}
        
                                </DropdownButton>

                            </div>

                            <div className='stack-select ms-auto' style={item.value.length != 0? ({visibility:"visible"}):({visibility:"hidden"})}>
                                <Stack direction='horizontal'>
                                    <Form.Select id = {item.fileName} value = {selectedFiles[item.fileName]} onChange = {(event) => {handleSelectChange(item.fileName, event.currentTarget.value)}} size="sm"> 
                                        <option disabled selected>Uploaded File(s)</option>
                                        {Array.isArray(item.value) && item.value.map((fileName, index) => (
                                        
                                            <option key = {index} value = {fileName}>{fileName.split("/")[1]}</option>
                                        ))} 
                                    </Form.Select>

                                    <a className = "ms-1" onClick = {() => {setClickedRemove(index)}}><Image alt = "Delete" src= {deleteIcon} fluid/></a>
                                    <a className = "ms-1" onClick = {() => {setClickedPreview(index)}}><Image alt = "View" src= {viewIcon} fluid/></a>
                                </Stack>
                            </div>

                        </Stack>
                    </Col>
                </Row>
            )}</>))}


            <Row  xs = {2} sm={2} md = {2} className='g-4 m-3'>
                <Col className='text-start'>
                    <Link className="mt-auto" onClick = {back}><SubmitButton text = "<< Back" width = "52"/></Link>
                </Col>
                <Col className='text-end'>
                    <Link to = "/Portal/Review" className="mt-auto"><SubmitButton text = "Finish and Review >>" width = "52"/></Link>
                </Col>
            </Row>
        </Container>

    )
};

export default LoanFiles;